import { Form } from "@unform/web";
import { GitCompare, Pencil, X } from "lucide-react";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { toast, Zoom } from "react-toastify";
import FormHeader from "~/components/RegisterForm/FormHeader";
import Input from "~/components/RegisterForm/Input";
import InputLine from "~/components/RegisterForm/InputLine";
import InputLineGroup from "~/components/RegisterForm/InputLineGroup";
import RegisterFormMenu from "~/components/RegisterForm/Menu";
import SelectPopover from "~/components/RegisterForm/SelectPopover";
import Textarea from "~/components/RegisterForm/Textarea";
import FormLoading from "~/components/RegisterForm/FormLoading";
import Preview from "~/components/Preview";
import { FullGridContext } from "../../..";
import { getRegistries, handleUpdatedFields } from "~/functions";
import api from "~/services/api";

export const InputContext = createContext({});

const balanceOptions = [
  { label: "Increase", value: "increase" },
  { label: "Decrease", value: "decrease" },
  { label: "None", value: "none" },
  { label: "Paid", value: "paid" },
  { label: "Non-paid day", value: "non-paid" },
];

export default function PagePreview({
  access,
  id,
  defaultFormType = "preview",
}) {
  const { handleOpened, setOpened, successfullyUpdated, setSuccessfullyUpdated } =
    useContext(FullGridContext);
  const [pageData, setPageData] = useState({
    name: "",
    code: "",
    description: "",
    affects_balance: balanceOptions[0].value,
    is_active: true,
    loaded: false,
  });
  const [registry, setRegistry] = useState({
    created_by: null,
    created_at: null,
    updated_by: null,
    updated_at: null,
    canceled_by: null,
    canceled_at: null,
  });
  const [formType, setFormType] = useState(defaultFormType);
  const [fullscreen, setFullscreen] = useState(false);
  const [activeMenu, setActiveMenu] = useState("general");
  const generalForm = useRef();

  function handleCloseForm() {
    if (!successfullyUpdated) {
      toast("Changes discarted!", { autoClose: 1000 });
    }
    handleOpened(null);
  }

  function handleGeneralFormSubmit(data) {
    if (successfullyUpdated) {
      toast("No need to be saved!", {
        autoClose: 1000,
        type: "info",
        transition: Zoom,
      });
      return;
    }

    const affectsBalanceValue =
      typeof data.affects_balance === "object"
        ? data.affects_balance?.value
        : data.affects_balance || pageData.affects_balance;

    const payload = {
      name: data.name,
      code: data.code,
      description: data.description || "",
      affects_balance: affectsBalanceValue,
      is_active: pageData.is_active,
    };

    if (!payload.name || !payload.code || !payload.affects_balance) {
      toast("Fill required fields to continue.", {
        autoClose: 1500,
        type: "error",
      });
      return;
    }

    if (id === "new") {
      api
        .post(`/employeemovements`, payload)
        .then((response) => {
          const newId = response.data?.id || null;
          setOpened(newId);
          setPageData({ ...pageData, ...payload });
          setSuccessfullyUpdated(true);
          toast("Saved!", { autoClose: 1000 });
          handleOpened(null);
        })
        .catch((err) => {
          if (err?.response?.data?.error) {
            toast(err.response.data.error, { type: "error", autoClose: 3000 });
          }
        });
    } else {
      const updated = handleUpdatedFields(payload, pageData);
      if (updated.length > 0) {
        const objUpdated = Object.fromEntries(updated);
        objUpdated.affects_balance = payload.affects_balance;
        console.log("Update payload:", objUpdated);
        api
          .put(`/employeemovements/${id}`, objUpdated)
          .then((response) => {
            setPageData({ ...pageData, ...objUpdated });
            setSuccessfullyUpdated(true);
            toast("Saved!", { autoClose: 1000 });
            handleOpened(null);
          })
          .catch((err) => {
            if (err?.response?.data?.error) {
              toast(err.response.data.error, {
                type: "error",
                autoClose: 3000,
              });
            }
          });
      }
    }
  }

  useEffect(() => {
    if (id === "new") {
      setFormType("full");
      setPageData((prev) => ({ ...prev, loaded: true }));
    } else {
      async function fetchMovement() {
        try {
          const { data } = await api.get(`/employeemovements/${id}`);
          const {
            created_by,
            created_at,
            updated_by,
            updated_at,
            canceled_by,
            canceled_at,
          } = data;
          const registries = await getRegistries({
            created_by,
            created_at,
            updated_by,
            updated_at,
            canceled_by,
            canceled_at,
          });
          setRegistry(registries);
          setPageData((prev) => ({
            ...prev,
            ...data,
            loaded: true,
          }));
        } catch (err) {
          if (err?.response?.data?.error) {
            toast(err.response.data.error, { type: "error", autoClose: 3000 });
          }
        }
      }
      fetchMovement();
    }
  }, [id]);

  const defaultAffectsBalance = balanceOptions.find(
    (opt) => opt.value === pageData.affects_balance
  );

  return (
    <Preview fullscreen={fullscreen} formType={formType}>
      <div className="flex flex-row items-start justify-between gap-4 h-full w-full">
        {formType !== "full" ? (
          <div className="flex flex-1 flex-col items-start justify-start text-sm">
            <div className="relative flex flex-col items-start justify-start">
              <button
                onClick={() => setFormType("full")}
                className="absolute top-2 right-20 text-md font-bold bg-mila_orange text-white rounded-md p-1 px-2 h-6 flex flex-row items-center justify-center text-xs gap-1"
              >
                <Pencil size={16} color="#fff" /> Open
              </button>
              <button
                onClick={() => handleOpened(null)}
                className="absolute top-2 right-2 text-md font-bold bg-secondary rounded-md p-1 px-2 h-6 flex flex-row items-center justify-center text-xs gap-1"
              >
                <X size={16} /> Close
              </button>
              <h2 style={{ fontSize: 24 }}>{pageData.name}</h2>
            </div>
          </div>
        ) : (
          <div className="flex h-full w-full flex-row items-start justify-between gap-4">
            <div className="flex flex-col items-center justify-between text-xs w-32 gap-4">
              <RegisterFormMenu
                setActiveMenu={setActiveMenu}
                activeMenu={activeMenu}
                name="general"
              >
                <GitCompare size={16} /> General
              </RegisterFormMenu>
            </div>
            <div className="border h-full rounded-xl overflow-hidden flex flex-1 flex-col justify-start">
              <div className="flex flex-col items-start justify-start text-sm overflow-y-scroll">
                <Form
                  ref={generalForm}
                  onSubmit={handleGeneralFormSubmit}
                  className="w-full"
                >
                  <InputContext.Provider
                    value={{
                      id,
                      generalForm,
                      setSuccessfullyUpdated,
                      fullscreen,
                      setFullscreen,
                      successfullyUpdated,
                      handleCloseForm,
                    }}
                  >
                    {id === "new" || pageData.loaded ? (
                      <>
                        <FormHeader
                          access={access}
                          title={pageData.name || "New Movement"}
                          registry={registry}
                          InputContext={InputContext}
                          createText="Save Movement"
                        />

                        <InputLineGroup
                          title="GENERAL"
                          activeMenu={activeMenu === "general"}
                        >
                          <InputLine title="Movement Information">
                            <Input
                              type="text"
                              name="name"
                              required
                              title="Name"
                              grow
                              defaultValue={pageData.name}
                              InputContext={InputContext}
                            />
                            <Input
                              type="text"
                              name="code"
                              required
                              title="Code"
                              grow
                              defaultValue={pageData.code}
                              InputContext={InputContext}
                            />
                          </InputLine>

                          <InputLine title="">
                            <SelectPopover
                              name="affects_balance"
                              title="Balance Effect"
                              required
                              options={balanceOptions}
                              defaultValue={defaultAffectsBalance}
                              value={defaultAffectsBalance}
                              InputContext={InputContext}
                              setReturnPopover={(opt) =>
                                setPageData((prev) => ({
                                  ...prev,
                                  affects_balance: opt?.value || null,
                                }))
                              }
                              grow
                            />
                          </InputLine>

                          <InputLine title="Description">
                            <Textarea
                              name="description"
                              title="Description"
                              rows="3"
                              grow
                              defaultValue={pageData.description}
                              InputContext={InputContext}
                            />
                          </InputLine>
                        </InputLineGroup>
                      </>
                    ) : (
                      <FormLoading />
                    )}
                  </InputContext.Provider>
                </Form>
              </div>
            </div>
          </div>
        )}
      </div>
    </Preview>
  );
}
